import logger from '../lib/logger';
import { supabase, AuthUserWithProfile } from '../lib/supabase';
import sessionService from './sessionService';
import { validateDemoLogin, isDemoUser, DEMO_CONFIG } from '../demo/demoUsers';
import { DEMO_SESSION_KEY, DEMO_SESSION_VERSION, DEMO_USER_KEY, DEMO_ORGANIZATION_KEY, DEMO_SESSION_TTL_MS, PILOT_TEST_ACCOUNT_EMAIL, PILOT_TEST_ACCOUNT_ENABLED, PILOT_ADMIN_ACCOUNT_EMAIL } from '../config/demoConfig';

// 🚨 ALDRIG använda service_role i frontend - endast anon key
// Denna fil använder ENDAST supabase anon client

// === DEMO ORGANIZATION (DEV ONLY) ===
export const DEMO_ORGANIZATION = {
  id: 'demo-org-001',
  name: 'CaseFlow Demo Organization',
  slug: 'caseflow-demo',
  plan: 'enterprise',
  created_at: new Date().toISOString(),
};

// === DEMO SESSION TYPES ===
export interface DemoSession {
  version: number;
  user: AuthUserWithProfile;
  expiresAt: number;
  createdAt: number;
}

// === DEMO SESSION PERSISTENCE ===
const saveDemoSession = (user: AuthUserWithProfile): void => {
  if (!import.meta.env.DEV && !DEMO_CONFIG.enabled && !PILOT_TEST_ACCOUNT_ENABLED) return;
  
  try {
    const session: DemoSession = {
      version: DEMO_SESSION_VERSION,
      user,
      expiresAt: Date.now() + DEMO_SESSION_TTL_MS,
      createdAt: Date.now(),
    };
    
    localStorage.setItem(DEMO_SESSION_KEY, JSON.stringify(session));
    logger.audit('DEMO_SESSION_SAVED', { userId: user.id.substring(0, 8) + '...' });
  } catch (error) {
    logger.error('Kunde inte spara demo-session', { error: String(error) });
  }
};

const getDemoSession = (): DemoSession | null => {
  // Defensive: safely check if we're in demo mode without crashing
  let isDev = false;
  let isDemoEnabled = false;
  let isPilotTestAccount = false;
  try {
    isDev = !!import.meta.env.DEV;
    isDemoEnabled = !!DEMO_CONFIG.enabled;
    isPilotTestAccount = PILOT_TEST_ACCOUNT_ENABLED;
  } catch {
    return null;
  }
  
  if (!isDev && !isDemoEnabled && !isPilotTestAccount) {
    return null;
  }
  
  try {
    // Defensive: safely read from localStorage (may throw in some browsers/security settings)
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(DEMO_SESSION_KEY);
    } catch (readError) {
      return null;
    }
    
    if (!stored) {
      return null;
    }
    
    const session: DemoSession = JSON.parse(stored);
    
    // Validate session shape
    if (!session.user || !session.user.id || !session.user.email) {
      try { localStorage.removeItem(DEMO_SESSION_KEY); } catch {}
      return null;
    }

    if (isPilotTestAccount && ![PILOT_TEST_ACCOUNT_EMAIL, PILOT_ADMIN_ACCOUNT_EMAIL].includes(session.user.email)) {
      try { localStorage.removeItem(DEMO_SESSION_KEY); } catch {}
      return null;
    }
    
    // Check version
    if (!session.version || session.version < DEMO_SESSION_VERSION) {
      try { localStorage.removeItem(DEMO_SESSION_KEY); } catch {}
      return null;
    }
    
    // Check if session is expired
    if (Date.now() > session.expiresAt) {
      try { localStorage.removeItem(DEMO_SESSION_KEY); } catch {}
      return null;
    }
    
    return session;
  } catch (error) {
    try { localStorage.removeItem(DEMO_SESSION_KEY); } catch {}
    return null;
  }
};

const clearDemoSession = (): void => {
  // Defensive: check demo mode safely
  try {
    if (!import.meta.env.DEV && !DEMO_CONFIG.enabled && !PILOT_TEST_ACCOUNT_ENABLED) return; // Only in DEV, demo, or pilot test-account mode
  } catch {
    return;
  }
  
  try {
    localStorage.removeItem(DEMO_SESSION_KEY);
    logger.audit('DEMO_SESSION_CLEARED', {});
  } catch (error) {
    logger.error('Kunde inte rensa demo-session', { error: String(error) });
  }
};

// === USERNAME-TO-EMAIL MAPPING FOR DEMO USERS ===
// Allow users to login with username instead of full email
const usernameToEmail = {
  'Test': 'test@caseflow.demo',
  'Admin': 'admin@caseflow.demo',
  'Agent': 'agent@caseflow.demo',
  'Technician': 'technician@caseflow.demo',
  'Viewer': 'viewer@caseflow.demo',
};

// Normalize login input: trim and handle case-insensitive username matching
// For DEV bypass, also normalize "test" (any case) to "test@caseflow.demo"
const normalizeLoginInput = (emailOrUsername: string): string => {
  const normalizedLogin = emailOrUsername.trim();
  
  // For DEV bypass: if user types "test" (any case), map to test@caseflow.demo
  if (normalizedLogin.toLowerCase() === 'test') {
    return 'test@caseflow.demo';
  }
  
  // If it's already an email (contains @), return as-is
  if (normalizedLogin.includes('@')) return normalizedLogin;
  
  // Check if it's a known username (case-insensitive)
  const lowerInput = normalizedLogin.toLowerCase();
  const matchedKey = Object.keys(usernameToEmail).find(
    key => key.toLowerCase() === lowerInput
  );
  
  if (matchedKey) {
    const mapped = usernameToEmail[matchedKey];
    logger.audit('USERNAME_MAPPED', { username: normalizedLogin, email: mapped.replace(/(.{2}).*@/, '$1***@') });
    return mapped;
  }
  
  return normalizedLogin;
};

export interface UserProfile {
  id: string;
  email: string;
  full_name?: string;
  role: 'admin' | 'technician' | 'agent' | 'viewer';
  created_at: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  plan?: string;
}

export interface AuthResult {
  success: boolean;
  error?: string;
  user?: AuthUserWithProfile;
  accessToken?: string;
}

// Generic error messages (ingen enumeration)
const LOGIN_ERRORS = {
  INVALID_CREDENTIALS: 'Felaktig email eller lösenord',
  EMAIL_NOT_CONFIRMED: 'Email-adressen har inte bekräftats',
  TOO_MANY_REQUESTS: 'För många inloggningsförsök. Försök igen senare.',
  UNKNOWN_ERROR: 'Ett oväntat fel inträffade. Försök igen.',
};

/**
 * Skapa en mock-användare för demo-inloggning
 * Genererar ett konsekvent UUID baserat på email
 */
// Cache for demo users to ensure stable references (prevents re-render loops)
const demoUserCache = new Map<string, AuthUserWithProfile>();

const createDemoUser = (email: string, role: string, name: string): AuthUserWithProfile => {
  // Return cached version if exists (stable reference)
  const cacheKey = `${email}-${role}`;
  const cached = demoUserCache.get(cacheKey);
  if (cached) return cached;
  
  // Skapa en enkel hash för att generera ett mock UUID (utan Date.now för stabilitet)
  const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const mockId = `demo-${hash}-${role}`;
  
  const user: AuthUserWithProfile = {
    id: mockId,
    email: email,
    organization_id: DEMO_ORGANIZATION.id,
    profile: {
      full_name: name,
      role: role as 'admin' | 'technician' | 'agent' | 'viewer',
    },
    organization: { ...DEMO_ORGANIZATION },
    email_verified: true,
  };
  
  // Cache the user object for stable references
  demoUserCache.set(cacheKey, user);
  return user;
};

/**
 * Logga in med email och lösenord
 * @param email - Användarens email eller användarnamn
 * @param password - Användarens lösenord
 * @returns AuthResult med success/error
 */
export const signInWithEmail = async (
  email: string,
  password: string
): Promise<AuthResult> => {
  try {
    // Normalize login input BEFORE any checks
    // Handles: trim, case-insensitive "Test" -> "test@caseflow.demo", username mapping
    const normalizedEmail = normalizeLoginInput(email);

    // Validera input
    if (!normalizedEmail || !password) {
      return { success: false, error: 'Email och lösenord krävs' };
    }

    // Enkel email-validering (inte för strikt för att undvika läckage)
    // För demo-användare, tillåt inloggning utan @-check
    const isDemoLogin = (import.meta.env.DEV || DEMO_CONFIG.enabled) && isDemoUser(normalizedEmail);
    if (!normalizedEmail.includes('@') && !isDemoLogin) {
      return { success: false, error: 'Ogiltig email-adress' };
    }

    logger.audit('LOGIN_ATTEMPT', { email: normalizedEmail.replace(/(.{2}).*@/, '$1***@') }); // Maskera email i log

    // === LOCAL DEV TEST ACCOUNT BYPASS ===
    // Endast för lokal utveckling - Test/Test konto
    // Detta får ALDRIG köras i produktion
    // Accepts: username "Test" (any case) or email "test@caseflow.demo" with password "Test"
    const pilotDemoUser = PILOT_TEST_ACCOUNT_ENABLED
      ? validateDemoLogin(normalizedEmail, password)
      : null;
    const isTestAccountLogin = normalizedEmail === PILOT_TEST_ACCOUNT_EMAIL && password === 'Test';
    const isAllowedPilotLogin = pilotDemoUser?.email === PILOT_ADMIN_ACCOUNT_EMAIL;

    if ((import.meta.env.DEV || DEMO_CONFIG.enabled || PILOT_TEST_ACCOUNT_ENABLED) && (isTestAccountLogin || isAllowedPilotLogin)) {
      const demoUser = pilotDemoUser;
      if (!isTestAccountLogin && !demoUser) return { success: false, error: LOGIN_ERRORS.INVALID_CREDENTIALS };

      const mockUser = demoUser
        ? createDemoUser(demoUser.email, demoUser.role, demoUser.name)
        : createDemoUser(PILOT_TEST_ACCOUNT_EMAIL, 'admin', 'Test User');

      logger.audit('LOCAL_DEV_LOGIN', { email: mockUser.email.replace(/(.{2}).*@/, '$1***@'), role: mockUser.profile?.role });
      
      // Save demo session for persistence
      saveDemoSession(mockUser);
      
      // Initiera session-timeout för demo-läge
      sessionService.initializeSession({
        onIdleWarning: () => {
          console.warn('Sessionsvarning: Du loggas ut snart på grund av inaktivitet.');
        },
        onSessionLogout: () => {
          signOut();
          window.location.href = '/login?reason=idle';
        },
      });
      
      return { success: true, user: mockUser, accessToken: 'demo-token' };
    }

    // === DEMO LOGIN CHECK ===
    // Kontrollera om det är en demo-användare och vi är i demo-läge
    if (DEMO_CONFIG.enabled && isDemoUser(normalizedEmail)) {
      logger.audit('DEMO_LOGIN_ATTEMPT', { email: normalizedEmail.replace(/(.{2}).*@/, '$1***@') });
      
      const demoUser = validateDemoLogin(normalizedEmail, password);
      
      if (demoUser) {
        logger.audit('DEMO_LOGIN_SUCCESS', { email: email.replace(/(.{2}).*@/, '$1***@'), role: demoUser.role });
        
        // Skapa mock-användare för demo
        const mockUser = createDemoUser(demoUser.email, demoUser.role, demoUser.name);
        
        // Save demo session for persistence
        saveDemoSession(mockUser);
        
        // Initiera session-timeout för demo-läge
        sessionService.initializeSession({
          onIdleWarning: () => {
            console.warn('Sessionsvarning: Du loggas ut snart på grund av inaktivitet.');
          },
          onSessionLogout: () => {
            signOut();
            window.location.href = '/login?reason=idle';
          },
        });
        
        return { success: true, user: mockUser, accessToken: 'demo-token' };
      } else {
        logger.audit('DEMO_LOGIN_FAILED', { email: email.replace(/(.{2}).*@/, '$1***@') });
        return { success: false, error: LOGIN_ERRORS.INVALID_CREDENTIALS };
      }
    }

    // === NORMAL SUPABASE AUTH ===
    // Check if supabase is configured
    if (!supabase) {
      logger.error('Försök till inloggning men Supabase är inte konfigurerat', {});
      return { success: false, error: 'Inloggning är inte tillgänglig just nu. Försök igen senare.' };
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });

    if (error) {
      logger.error('LOGIN_FAILED', { error: error.message, email: '***' });
      
      // Mappa Supabase felmeddelanden till användarvänliga meddelanden (generic - ingen enumeration)
      // VIKTIGT: Använd inte Supabase's exakta felmeddelanden i koden för att undvika enumeration
      const errorMsg = error.message.toLowerCase();
      
      // Check for email not confirmed (utan att använda exakta strängen)
      if (errorMsg.includes('email') && errorMsg.includes('confirm')) {
        return { success: false, error: LOGIN_ERRORS.EMAIL_NOT_CONFIRMED };
      }
      
      // Check for too many requests
      if (errorMsg.includes('too') && errorMsg.includes('request')) {
        return { success: false, error: LOGIN_ERRORS.TOO_MANY_REQUESTS };
      }
      
      // Generic error för alla andra fel (inklusive invalid credentials)
      return { success: false, error: LOGIN_ERRORS.INVALID_CREDENTIALS };
    }

    if (!data.user || !data.session) {
      return { success: false, error: 'Ingen användare hittades' };
    }

    // Kontroll: Email verification
    const isEmailVerified = data.user.email_confirmed_at != null;
    if (!isEmailVerified) {
      logger.audit('LOGIN_FAILED - email not verified', { userId: data.user.id });
      return { success: false, error: 'Email-adressen har inte bekräftats. Kontrollera din inkorg.' };
    }

    logger.audit('LOGIN_SUCCESS', { userId: data.user.id, email: '***' });

    // DIAGNOSTICS: Log session details from signInWithPassword result
    const returnedSessionInfo = {
      userId: data.user.id.substring(0, 8) + '...',
      hasAccessToken: !!data.session?.access_token,
      hasRefreshToken: !!data.session?.refresh_token,
      tokenExp: data.session?.expires_at ? new Date(data.session.expires_at * 1000).toISOString() : 'none',
      expiresIn: data.session?.expires_in
    };
    logger.info('AUTH_LOGIN_RETURNED_SESSION', returnedSessionInfo);

    // DIAGNOSTICS: Log login return
    console.log(
      "LOGIN RETURNED SESSION",
      !!data.session,
      data.session?.access_token?.length
    );

    // CRITICAL FIX: Explicitly set session to ensure it's available in client auth state
    // This is required because signInWithPassword doesn't always propagate session reliably
    const { error: setSessionError } = await supabase.auth.setSession({
      access_token: data.session.access_token,
      refresh_token: data.session.refresh_token,
    });
    
    if (setSessionError) {
      logger.error('Failed to set session after login', { error: setSessionError.message });
    } else {
      logger.audit('AUTH_SESSION_EXPLICITLY_SET', {
        userId: data.user.id.substring(0, 8) + '...',
        hasAccessToken: !!data.session.access_token
      });
    }

    // DIAGNOSTICS: Check session after setSession
    const { data: sessionCheckData } = await supabase.auth.getSession();
    console.log(
      "SESSION AFTER SETSESSION",
      !!sessionCheckData?.session
    );

    // DIAGNOSTICS: Check localStorage
    const storedAuth = localStorage.getItem('caseflow-auth');
    console.log(
      "LOCALSTORAGE AFTER LOGIN",
      !!storedAuth,
      storedAuth ? 'has data' : 'empty'
    );

    // Initiera session-timeout efter lyckad inloggning
    sessionService.initializeSession({
      onIdleWarning: () => {
        console.warn('Sessionsvarning: Du loggas ut snart på grund av inaktivitet.');
      },
      onSessionLogout: () => {
        // Rensa auth state vid idle timeout
        signOut();
        window.location.href = '/login?reason=idle';
      },
    });

    // CRITICAL FIX: Always preserve accessToken from login response
    // Do NOT rely on getSession() - it may return null due to race condition
    let hasSession = true; // We have a valid session from login
    let accessToken = data.session?.access_token || null;
    let authUserId = data.user.id;
    
    // DIAGNOSTICS: Log session state before profile/organization queries
    logger.info('AUTH_QUERY_PRECHECK', {
      userId: authUserId.substring(0, 8) + '...',
      hasSession,
      hasAccessToken: !!accessToken,
      tokenSource: 'login_response',
      queryWillUseAuth: !!accessToken
    });

    // Hämta profil och organisation
    const userWithProfile: AuthUserWithProfile = {
      id: data.user.id,
      email: data.user.email || normalizedEmail,
    };

    // Försök hämta profil (men blockera inte inloggning om det misslyckas)
    // CRITICAL FIX: Pass accessToken directly to avoid getSession() race condition
    if (accessToken) {
      try {
        const profile = await getUserProfile(data.user.id, accessToken);
        if (profile) {
          userWithProfile.profile = {
            full_name: profile.full_name,
            role: profile.role,
          };
        }
      } catch (profileError) {
        logger.warn('Kunde inte hämta profil vid inloggning', { error: String(profileError) });
      }

      // Försök hämta organisation (men blockera inte inloggning)
      try {
        const organization = await getUserOrganization(data.user.id, accessToken);
        if (organization) {
          userWithProfile.organization = {
            id: organization.id,
            name: organization.name,
            slug: organization.slug,
          };
        }
      } catch (orgError) {
        logger.warn('Kunde inte hämta organisation vid inloggning', { error: String(orgError) });
      }
    } else {
      // Fallback: try without token (will use getSession() internally)
      logger.warn('No accessToken, using fallback getSession() for profile/org queries', {
        userId: data.user.id.substring(0, 8) + '...'
      });
      
      try {
        const profile = await getUserProfile(data.user.id);
        if (profile) {
          userWithProfile.profile = {
            full_name: profile.full_name,
            role: profile.role,
          };
        }
      } catch (profileError) {
        logger.warn('Kunde inte hämta profil vid inloggning', { error: String(profileError) });
      }

      try {
        const organization = await getUserOrganization(data.user.id);
        if (organization) {
          userWithProfile.organization = {
            id: organization.id,
            name: organization.name,
            slug: organization.slug,
          };
        }
      } catch (orgError) {
        logger.warn('Kunde inte hämta organisation vid inloggning', { error: String(orgError) });
      }
    }

    return { success: true, user: userWithProfile, accessToken };
  } catch (error) {
    logger.error('Oväntat fel vid inloggning', { error: String(error) });
    return { success: false, error: LOGIN_ERRORS.UNKNOWN_ERROR };
  }
};

/**
 * Logga ut användaren
 * Rensar session och känslig data
 */
export const signOut = async (): Promise<void> => {
  try {
    logger.audit('LOGOUT', {});
    
    // Clear demo session (DEV ONLY)
    clearDemoSession();
    
    // Använd sessionService för säker utloggning
    sessionService.secureLogout();
    
    // Supabase signOut (only if configured)
    if (supabase) {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        logger.error('Fel vid utloggning från Supabase', { error: error.message });
      }
    } else {
      logger.info('Supabase inte konfigurerat, hoppar över Supabase signOut', {});
    }

    // Rensa sessionStorage (där auth-token lagras)
    sessionStorage.removeItem('caseflow-auth');
    
    // Rensa eventuell känslig data från localStorage (förutom theme/locale)
    const allowedKeys = ['theme', 'locale', 'tenant-id', 'caseflow-auth', 'demo-session', 'demo-session-version'];
    Object.keys(localStorage).forEach(key => {
      if (!allowedKeys.includes(key)) {
        localStorage.removeItem(key);
      }
    });

    // Städa session timers
    sessionService.cleanupSession();

    logger.audit('LOGOUT_COMPLETED', {});
  } catch (error) {
    logger.error('Oväntat fel vid utloggning', { error: String(error) });
    // Tvinga rensning även vid fel
    sessionStorage.clear();
  }
};

/**
 * Hämta aktuell session
 * @returns session eller null
 */
export const getCurrentSession = async () => {
  try {
    // DIAGNOSTICS: Log session check start
    console.log('GETCURRENTSESSION_START', {
      timestamp: new Date().toISOString(),
      isDev: import.meta.env.DEV,
      isDemoConfig: DEMO_CONFIG.enabled,
      hasSupabase: !!supabase
    });
    
    logger.audit('AUTH_GET_SESSION_START', { timestamp: new Date().toISOString() });
    
    // CRITICAL FIX: Check Supabase session FIRST, before demo session
    // This ensures real Pilot sessions are always prioritized over demo sessions
    // Supabase session persistence is configured in src/lib/supabase.ts with localStorage
    
    // Check if supabase is configured
    if (supabase) {
      // DIAGNOSTICS: Check localStorage before getSession
      const storedAuthBefore = localStorage.getItem('caseflow-auth');
      console.log('GETSESSION_LOCALSTORAGE_CHECK', {
        hasStoredAuth: !!storedAuthBefore,
        storedAuthLength: storedAuthBefore?.length || 0
      });
      
      const { data: { session }, error } = await supabase.auth.getSession();
      
      // DIAGNOSTICS: Log getSession result
      console.log('GETSESSION_RESULT', {
        hasSession: !!session,
        hasError: !!error,
        errorMessage: error?.message,
        userId: session?.user?.id?.substring(0, 8) + '...' || 'none'
      });
      
      if (error) {
        logger.error('Kunde inte hämta session', { error: error.message });
        logger.audit('AUTH_SUPABASE_SESSION_ERROR', { error: error.message });
        // Continue to check demo session as fallback
      }

      if (session) {
        logger.audit('AUTH_SUPABASE_SESSION_FOUND', { userId: session.user?.id?.substring(0, 8) + '...' || 'unknown' });
        return session;
      }
      
      logger.audit('AUTH_SUPABASE_SESSION_NOT_FOUND', {});
    }
    
    // CRITICAL FIX: Only check demo session as FALLBACK when no Supabase session exists
    // This prevents demo mode from interfering with real Pilot sessions
    // Demo session is only restored in DEV mode or when explicitly enabled
    if (import.meta.env.DEV || DEMO_CONFIG.enabled || PILOT_TEST_ACCOUNT_ENABLED) {
      const demoSession = getDemoSession();
      
      if (demoSession) {
        logger.audit('DEMO_SESSION_RESTORED', { userId: demoSession.user.id.substring(0, 8) + '...' });
        logger.audit('AUTH_SESSION_FOUND', { type: 'demo', userId: demoSession.user.id.substring(0, 8) + '...' });
        return {
          user: {
            id: demoSession.user.id,
            email: demoSession.user.email,
            organization_id: demoSession.user.organization?.id || DEMO_ORGANIZATION.id,
            organization: demoSession.user.organization || { ...DEMO_ORGANIZATION },
            email_verified: demoSession.user.email_verified || true,
          },
          expires_at: Math.floor(demoSession.expiresAt / 1000),
        };
      }
      
      logger.audit('AUTH_NO_DEMO_SESSION', {});
    }
    
    // No session found - either not logged in or Supabase not configured
    if (!supabase) {
      logger.info('Supabase inte konfigurerat, kan inte hämta session', {});
    }

    return null;
  } catch (error) {
    logger.error('Ovantat fel vid hämntning av session', { error: String(error) });
    return null;
  }
};

/**
 * Hämta aktuell användare
 * @returns user eller null
 */
export const getCurrentUser = async () => {
  try {
    // CRITICAL FIX: Check Supabase user FIRST, before demo session
    // This ensures real Pilot users are always prioritized over demo users
    
    // Check if supabase is configured
    if (supabase) {
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) {
        logger.error('Kunde inte hämta användare', { error: error.message });
        // Continue to check demo as fallback
      }

      if (user) {
        logger.audit('AUTH_SUPABASE_USER_FOUND', { userId: user.id?.substring(0, 8) + '...' || 'unknown' });
        return user;
      }
      
      logger.audit('AUTH_SUPABASE_USER_NOT_FOUND', {});
    }
    
    // CRITICAL FIX: Only check demo user as FALLBACK when no Supabase user exists
    // This prevents demo mode from interfering with real Pilot users
    if (import.meta.env.DEV || DEMO_CONFIG.enabled || PILOT_TEST_ACCOUNT_ENABLED) {
      const demoSession = getDemoSession();
      if (demoSession) {
        logger.audit('DEMO_USER_RESTORED', { userId: demoSession.user.id.substring(0, 8) + '...' });
        // Return a mock user object with organization
        return {
          id: demoSession.user.id,
          email: demoSession.user.email,
          organization_id: demoSession.user.organization?.id || DEMO_ORGANIZATION.id,
          organization: demoSession.user.organization || { ...DEMO_ORGANIZATION },
          email_verified: demoSession.user.email_verified || true,
        };
      }
    }

    // No user found - either not logged in or Supabase not configured
    if (!supabase) {
      logger.info('Supabase inte konfigurerat, kan inte hämta användare', {});
    }

    return null;
  } catch (error) {
    logger.error('Oväntat fel vid hämtning av användare', { error: String(error) });
    return null;
  }
};

/**
 * Hämta användarens profil från databasen
 * @param userId - Användarens UUID
 * @param sessionToken - Optional session token from login response (bypasses getSession() check)
 * @returns UserProfile eller null
 */
export const getUserProfile = async (userId: string, sessionToken?: string): Promise<UserProfile | null> => {
  try {
    if (!userId) {
      logger.warn('getUserProfile anropad utan userId', {});
      return null;
    }

    // Om det är en demo-användare, returnera mock-profil
    if (userId.startsWith('demo-')) {
      // Extrahera roll från demo-ID:t
      const roleMatch = userId.match(/demo-\d+-(.+?)-\w+/);
      const role = roleMatch ? roleMatch[1] as 'admin' | 'technician' | 'agent' | 'viewer' : 'viewer';
      
      return {
        id: userId,
        email: 'demo@caseflow.demo',
        full_name: 'Demo User',
        role: role,
        created_at: new Date().toISOString(),
      };
    }

    // Check if supabase is configured
    if (!supabase) {
      logger.info('Supabase inte konfigurerat, kan inte hämta profil', { userId: userId.substring(0, 8) + '...' });
      return null;
    }

    // Use provided session token if available (from login response), otherwise check getSession()
    let hasSession = false;
    let accessToken = sessionToken || null;
    
    if (!accessToken) {
      // Fallback to getSession() only if no token provided
      const { data: sessionData } = await supabase.auth.getSession();
      hasSession = !!sessionData?.session;
      accessToken = sessionData?.session?.access_token;
    } else {
      hasSession = true;
    }
    
    const sessionUserId = userId; // When using provided token, assume it matches
    
    logger.info('AUTH_PROFILE_QUERY_PRECHECK', {
      targetUserId: userId.substring(0, 8) + '...',
      hasSession,
      hasAccessToken: !!accessToken,
      sessionMatchesTarget: true,
      sessionUserId: userId.substring(0, 8) + '...',
      tokenSource: sessionToken ? 'login_response' : 'getSession',
      clientId: supabase.supabaseKey?.substring(0, 8) + '...' || 'unknown'
    });

    // DIAGNOSTICS: Log what's about to be queried
    console.log('PROFILE_QUERY_START', {
      userId: userId.substring(0, 8) + '...',
      hasAccessToken: !!accessToken,
      tokenPreview: accessToken ? accessToken.substring(0, 20) + '...' : 'none',
      clientHasSession: !!supabase.auth.getSession()
    });

    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, full_name, role, created_at')
      .eq('id', userId)
      .single();

    // DIAGNOSTICS: Log query result
    console.log('PROFILE_QUERY_RESULT', {
      hasData: !!data,
      hasError: !!error,
      errorCode: error?.code,
      errorStatus: error?.status,
      errorMessage: error?.message
    });

    if (error) {
      // Handle both PGRST116 (PostgREST translated) and 406 HTTP status directly
      // PGRST116 = "could not find rows" - translated from 406 by Supabase client
      if (error.code === 'PGRST116' || error.status === 406) {
        logger.info('Ingen profil hittad för användare', { userId: userId.substring(0, 8) + '...' });
        return null;
      }
      logger.error('Kunde inte hämta profil', { error: error.message, userId: userId.substring(0, 8) + '...' });
      return null;
    }

    return data as UserProfile;
  } catch (error) {
    logger.error('Oväntat fel vid hämtning av profil', { error: String(error) });
    return null;
  }
};

/**
 * Hämta användarens organisation
 * @param userId - Användarens UUID
 * @param sessionToken - Optional session token from login response (bypasses getSession() check)
 * @returns Organization eller null
 */
export const getUserOrganization = async (userId: string, sessionToken?: string): Promise<Organization | null> => {
  try {
    if (!userId) {
      logger.warn('getUserOrganization anropad utan userId', {});
      return null;
    }

    // Om det är en demo-användare, returnera mock-organisation
    if (userId.startsWith('demo-')) {
      return { ...DEMO_ORGANIZATION };
    }

    // Check if supabase is configured
    if (!supabase) {
      logger.info('Supabase inte konfigurerat, kan inte hämta organisation', { userId: userId.substring(0, 8) + '...' });
      return null;
    }

    // Use provided session token if available (from login response), otherwise check getSession()
    let hasSession = false;
    let accessToken = sessionToken || null;
    
    if (!accessToken) {
      // Fallback to getSession() only if no token provided
      const { data: sessionData } = await supabase.auth.getSession();
      hasSession = !!sessionData?.session;
      accessToken = sessionData?.session?.access_token;
    } else {
      hasSession = true;
    }
    
    const sessionUserId = userId; // When using provided token, assume it matches
    
    logger.info('AUTH_ORG_QUERY_PRECHECK', {
      targetUserId: userId.substring(0, 8) + '...',
      hasSession,
      hasAccessToken: !!accessToken,
      sessionMatchesTarget: true,
      sessionUserId: userId.substring(0, 8) + '...',
      tokenSource: sessionToken ? 'login_response' : 'getSession',
      clientId: supabase.supabaseKey?.substring(0, 8) + '...' || 'unknown'
    });

    // DIAGNOSTICS: Log org membership query
    console.log('ORG_MEMBER_QUERY_START', {
      userId: userId.substring(0, 8) + '...',
      hasAccessToken: !!accessToken
    });

    // Hämta organisation_id först (undviker JOIN för att slippa RLS-rekursion)
    const { data: memberData, error: memberError } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', userId)
      .single();

    // DIAGNOSTICS: Log org membership query result
    console.log('ORG_MEMBER_QUERY_RESULT', {
      hasData: !!memberData,
      hasError: !!memberError,
      errorCode: memberError?.code,
      errorStatus: memberError?.status,
      errorMessage: memberError?.message
    });

    if (memberError) {
      // Handle both PGRST116 (PostgREST translated) and 406 HTTP status directly
      // PGRST116 = "could not find rows" - translated from 406 by Supabase client
      if (memberError.code === 'PGRST116' || memberError.status === 406) {
        logger.info('Användare tillhör ingen organisation', { userId: userId.substring(0, 8) + '...' });
        return null;
      }
      logger.error('Kunde inte hämta organisation', { error: memberError.message, userId: userId.substring(0, 8) + '...' });
      return null;
    }

    if (!memberData?.organization_id) {
      return null;
    }

    // Hämta organisation separat (undviker RLS-rekursion)
    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .select('id, name, slug, created_at')
      .eq('id', memberData.organization_id)
      .single();

    if (orgError) {
      logger.error('Kunde inte hämta organisationsdetaljer', { error: orgError.message, userId: userId.substring(0, 8) + '...' });
      return null;
    }

    if (!orgData) {
      return null;
    }
    
    return orgData as unknown as Organization;
  } catch (error) {
    logger.error('Oväntat fel vid hämtning av organisation', { error: String(error) });
    return null;
  }
};

/**
 * Hämta användarens roll
 * @param userId - Användarens UUID
 * @returns roll eller 'viewer' som default
 */
export const getUserRole = async (userId: string): Promise<string> => {
  try {
    const profile = await getUserProfile(userId);
    return profile?.role || 'viewer';
  } catch (error) {
    logger.error('Kunde inte hämta användarroll', { error: String(error) });
    return 'viewer'; // Säker default
  }
};

/**
 * Kontrollera om användarens email är verifierad
 * @param userId - Användarens UUID
 * @returns boolean
 */
export const isEmailVerified = async (userId: string): Promise<boolean> => {
  try {
    // För demo-användare, alltid verifierad
    if (userId.startsWith('demo-')) {
      return true;
    }

    // Check if supabase is configured
    if (!supabase) {
      logger.info('Supabase inte konfigurerat, kan inte kontrollera email verification', { userId: userId.substring(0, 8) + '...' });
      return false;
    }

    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error || !user) {
      return false;
    }

    return user.email_confirmed_at != null;
  } catch (error) {
    logger.error('Kunde inte kontrollera email verification', { error: String(error) });
    return false;
  }
};

/**
 * Uppdatera session (refresh)
 */
export const refreshSession = async () => {
  try {
    logger.audit('SESSION_REFRESH_ATTEMPT', {});
    
    // Check for demo session first (DEV ONLY)
    if (import.meta.env.DEV || DEMO_CONFIG.enabled || PILOT_TEST_ACCOUNT_ENABLED) {
      const demoSession = getDemoSession();
      if (demoSession) {
        logger.audit('SESSION_REFRESH_USING_DEMO_SESSION', {});
        return {
          user: {
            id: demoSession.user.id,
            email: demoSession.user.email,
            organization_id: demoSession.user.organization?.id || DEMO_ORGANIZATION.id,
            organization: demoSession.user.organization || { ...DEMO_ORGANIZATION },
            email_verified: demoSession.user.email_verified || true,
          },
          expires_at: Math.floor(demoSession.expiresAt / 1000),
        };
      }
    }

    // Check if supabase is configured
    if (!supabase) {
      logger.info('Supabase inte konfigurerat, kan inte uppdatera session', {});
      return null;
    }

    const { data, error } = await supabase.auth.refreshSession();
    
    if (error) {
      logger.error('SESSION_EXPIRED', { error: error.message });
      return null;
    }

    logger.audit('SESSION_REFRESH_SUCCESS', {});
    return data.session;
  } catch (error) {
    logger.error('Oväntat fel vid session refresh', { error: String(error) });
    return null;
  }
};

/**
 * Lyssna på auth-state ändringar
 * @param callback - Anropas vid auth-state ändring
 * @returns Unsubscribe funktion
 */
export const onAuthStateChange = (callback: (event: string, session: unknown) => void) => {
  // CRITICAL FIX: In demo mode, we must check if Supabase is configured FIRST
  // If Supabase is configured (real Pilot mode), use Supabase's native listener
  // Only use demo session fallback when Supabase is NOT configured
  
  const isSupabaseConfigured = !!supabase;
  const isDevOrDemo = import.meta.env.DEV || DEMO_CONFIG.enabled || PILOT_TEST_ACCOUNT_ENABLED;
  
  // If Supabase is configured, always use native Supabase listener
  if (isSupabaseConfigured) {
    logger.info('Supabase configured: Using native auth state listener', {});
    
    return supabase.auth.onAuthStateChange((event, session) => {
      logger.audit('AUTH_STATE_CHANGE_SUPABASE', { event, hasSession: !!session });
      
      // If we have a real Supabase session, use it directly
      if (session) {
        callback(event, session);
        return;
      }
      
      // Only fall back to demo session if in dev/demo mode AND no Supabase session
      // AND we explicitly want demo mode behavior
      if (!session && isDevOrDemo && !isSupabaseConfigured) {
        const demoSession = getDemoSession();
        if (demoSession) {
          logger.audit('AUTH_STATE_CHANGE_USING_DEMO_SESSION', { event });
          const mockSession = {
            user: {
              id: demoSession.user.id,
              email: demoSession.user.email,
              organization: demoSession.user.organization,
              email_verified: demoSession.user.email_verified || true,
            },
            expires_at: Math.floor(demoSession.expiresAt / 1000),
          };
          callback(event, mockSession);
          return;
        }
      }
      
      // No session - pass null to callback
      callback(event, session);
    });
  }
  
  // Supabase is NOT configured - use demo mode behavior
  if (isDevOrDemo) {
    logger.info('Demo mode (no Supabase): Creating custom auth state listener', {});
    
    // Check if we have a demo session immediately
    const demoSession = getDemoSession();
    if (demoSession) {
      // Fire callback with demo session after a short delay to allow initialization
      setTimeout(() => {
        logger.audit('AUTH_STATE_CHANGE_DEMO_SESSION_RESTORED', { event: 'INITIAL_SESSION' });
        const mockSession = {
          user: {
            id: demoSession.user.id,
            email: demoSession.user.email,
            organization_id: demoSession.user.organization?.id || DEMO_ORGANIZATION.id,
            organization: demoSession.user.organization || { ...DEMO_ORGANIZATION },
            email_verified: demoSession.user.email_verified || true,
          },
          expires_at: Math.floor(demoSession.expiresAt / 1000),
        };
        callback('INITIAL_SESSION', mockSession);
      }, 100);
    }
    
    // Return a mock subscription that can be unsubscribed
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            logger.info('Demo mode: Auth state listener unsubscribed', {});
          },
        },
      },
    };
  }
  
};
